import * as React from 'react';
import { getErrorMessage } from '@/api/client';
import { echoService } from '@/services/echoService';
import type { VideoAvatarVariant } from '@/types/video_avatar';

type VideoMode = 'photo' | 'idle' | `variant-${number}`;

const IDLE_POLL_INTERVAL_MS = 10_000;
const MAX_IDLE_POLL_ATTEMPTS = 126;

interface UseEchoProfileCardParams {
  draftId?: string | undefined;
  idleVideoUrl?: string | null | undefined;
  storageKey: string | null;
}

interface UseEchoProfileCardReturn {
  videoMode: VideoMode;
  persistMode: (mode: VideoMode) => void;
  variants: VideoAvatarVariant[];
  isGeneratingIdle: boolean;
  idleGenError: string | null;
  idleGenStarted: boolean;
  liveIdleVideoUrl: string | null;
  handleGenerateIdle: () => Promise<void>;
}

export function useEchoProfileCard({
  draftId,
  idleVideoUrl,
  storageKey,
}: UseEchoProfileCardParams): UseEchoProfileCardReturn {
  const [isGeneratingIdle, setIsGeneratingIdle] = React.useState(false);
  const [idleGenError, setIdleGenError] = React.useState<string | null>(null);
  const [idleGenStarted, setIdleGenStarted] = React.useState(false);
  const [liveIdleVideoUrl, setLiveIdleVideoUrl] = React.useState<string | null>(null);
  const [variants, setVariants] = React.useState<VideoAvatarVariant[]>([]);
  const pollRef = React.useRef<number | undefined>(undefined);
  const pollCountRef = React.useRef(0);

  const [videoMode, setVideoMode] = React.useState<VideoMode>(() => {
    if (!storageKey) return idleVideoUrl ? 'idle' : 'photo';
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved === 'photo') return 'photo';
      if (saved === 'idle' && idleVideoUrl) return 'idle';
      if (saved?.startsWith('variant-')) return saved as VideoMode;
      return idleVideoUrl ? 'idle' : 'photo';
    } catch {
      return idleVideoUrl ? 'idle' : 'photo';
    }
  });

  const persistMode = React.useCallback(
    (mode: VideoMode) => {
      setVideoMode(mode);
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, mode);
        } catch {
          /* ignore */
        }
      }
    },
    [storageKey]
  );

  // Fetch video avatar variants on mount and auto-select the greeting chosen in video step
  React.useEffect(() => {
    if (!draftId) return;
    echoService
      .getVideoAvatarStatus(draftId)
      .then((status) => {
        const done = status.variants.filter(
          (variant) => variant.status === 'done' && variant.video_url
        );
        setVariants(done);

        // Auto-select the greeting variant chosen in video step when no idle video exists
        if (!idleVideoUrl && status.selected_variant) {
          const hasSaved = storageKey && localStorage.getItem(storageKey);
          if (!hasSaved || hasSaved === 'idle') {
            const selected = done.find(
              (variant) => variant.variant_number === status.selected_variant
            );
            if (selected) {
              persistMode(`variant-${status.selected_variant}`);
            }
          }
        }
      })
      .catch(() => {
        /* ignore — no variants */
      });
  }, [draftId, idleVideoUrl, storageKey, persistMode]);

  const stopPolling = React.useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = undefined;
    }
  }, []);

  const startPolling = React.useCallback(
    (id: string) => {
      stopPolling();
      pollCountRef.current = 0;

      const poll = async () => {
        pollCountRef.current += 1;
        if (pollCountRef.current > MAX_IDLE_POLL_ATTEMPTS) {
          stopPolling();
          setIdleGenStarted(false);
          setIdleGenError(
            'Video generation is taking too long. Your echo is available without video; retry later.'
          );
          return;
        }

        try {
          const status = await echoService.getIdleVideoStatus(id);
          if (status.status === 'done' && status.idle_video_url) {
            stopPolling();
            setLiveIdleVideoUrl(status.idle_video_url);
            setIdleGenStarted(false);
            setIdleGenError(null);
            persistMode('idle');
          } else if (status.status === 'failed') {
            stopPolling();
            setIdleGenStarted(false);
            setIdleGenError(
              'Video generation failed. Your echo is available without video; retry later.'
            );
          } else if (status.status === 'none') {
            stopPolling();
            setIdleGenStarted(false);
            setIdleGenError('Video generation did not start. You can retry later.');
          }
        } catch {
          // A transient status request must not discard a running provider job.
        }
      };

      pollRef.current = window.setInterval(() => void poll(), IDLE_POLL_INTERVAL_MS);
      void poll();
    },
    [persistMode, stopPolling]
  );

  // Resume an idle-video job started during training or on a previous visit.
  React.useEffect(() => {
    if (!draftId) return;
    let cancelled = false;

    echoService
      .getIdleVideoStatus(draftId)
      .then((status) => {
        if (cancelled) return;
        if (status.status === 'done' && status.idle_video_url) {
          setLiveIdleVideoUrl(status.idle_video_url);
          setIdleGenStarted(false);
          setIdleGenError(null);
          return;
        }
        if (status.status === 'processing') {
          setIdleGenStarted(true);
          setIdleGenError(null);
          startPolling(draftId);
        }
      })
      .catch(() => {
        // The profile remains fully usable with its static image.
      });
    return () => {
      cancelled = true;
    };
  }, [draftId, startPolling]);

  // Cleanup polling on unmount
  React.useEffect(() => () => stopPolling(), [stopPolling]);

  const handleGenerateIdle = React.useCallback(async () => {
    if (!draftId) return;
    setIsGeneratingIdle(true);
    setIdleGenError(null);
    try {
      await echoService.generateIdleVideo(draftId);
      setIdleGenStarted(true);
      startPolling(draftId);
    } catch (e: unknown) {
      const msg = getErrorMessage(e);
      const status = (e as { status?: number } | null)?.status;
      if (status === 409 || msg.toLowerCase().includes('already in progress')) {
        setIdleGenStarted(true);
        startPolling(draftId);
      } else {
        setIdleGenError(msg || 'Failed to start idle video generation');
      }
    } finally {
      setIsGeneratingIdle(false);
    }
  }, [draftId, startPolling]);

  return {
    videoMode,
    persistMode,
    variants,
    isGeneratingIdle,
    idleGenError,
    idleGenStarted,
    liveIdleVideoUrl,
    handleGenerateIdle,
  };
}
